import onlineOrderSoundUrl from "../assets/sounds/online-order-ring.wav";
import callSoundUrl from "../assets/sounds/ring.wav";
import File from "../services/File";

import { toAudioVolume } from "./ringtone";

import type {
	SoundRef,
	SoundSettingValue,
	SoundsState,
} from "../redux/reducers/settings/sounds";

/**
 * Звуки диспетчерской: встроенные или загруженные в настройках файлы.
 * Файлы лежат в приватном бакете, читаются через file.download и кэшируются
 * как object URL по id файла.
 */

export const DEFAULT_ONLINE_ORDER_SOUND_URL = onlineOrderSoundUrl;
export const DEFAULT_CALL_SOUND_URL = callSoundUrl;

export const SOUND_MAX_SECONDS = 15;
export const SOUND_FORMATS = ["mp3", "wav", "ogg", "m4a", "aac"];
export const SOUND_ACCEPT = SOUND_FORMATS.map((ext) => `.${ext}`).join(", ");

const pendingUrls = new Map<number, Promise<string | null>>();
const loadedUrls = new Map<number, string>();

function loadSoundFile(fileId: number): Promise<string | null> {
	const pending = pendingUrls.get(fileId);
	if (pending) return pending;

	const request = File.download(fileId)
		.then((blob) => {
			if (!blob) throw new Error("empty file");
			const url = URL.createObjectURL(blob);
			loadedUrls.set(fileId, url);
			return url;
		})
		.catch((error) => {
			// Сбой не кэшируем: следующий запрос попробует скачать снова.
			pendingUrls.delete(fileId);
			// eslint-disable-next-line no-console
			console.warn("[notificationSound] download failed", fileId, error);
			return null;
		});

	pendingUrls.set(fileId, request);
	return request;
}

async function resolveSoundUrl(
	sound: SoundRef | null | undefined,
	fallbackUrl: string,
): Promise<string> {
	if (!sound?.fileId) return fallbackUrl;
	return (await loadSoundFile(sound.fileId)) ?? fallbackUrl;
}

/** Заранее скачивает файлы звонка, чтобы рингтон не ждал сети. */
export function preloadCallSounds(sounds: SoundsState) {
	[sounds.incomingCall, sounds.incomingCallDuringConversation].forEach(
		({ sound }) => {
			if (sound?.fileId) loadSoundFile(sound.fileId);
		},
	);
}

/**
 * Рингтон в общий audio-элемент звонка. Звонок нельзя задерживать, поэтому
 * пока файл не скачан — сразу звучит стандартный звук.
 */
export function playRingtone(
	element: HTMLAudioElement,
	{ volume, sound }: SoundSettingValue,
) {
	const fileId = sound?.fileId;
	const loadedUrl = fileId ? loadedUrls.get(fileId) : undefined;
	if (fileId && !loadedUrl) loadSoundFile(fileId);

	const url = loadedUrl ?? DEFAULT_CALL_SOUND_URL;

	// Второй входящий при уже звучащем рингтоне не должен его перезапускать.
	if (element.getAttribute("src") !== url) element.src = url;
	element.volume = toAudioVolume(volume);

	element.play().catch((error) => {
		// AbortError — звонок уже приняли или сбросили, NotAllowedError —
		// автоплей заблокирован до первого действия пользователя.
		if (error?.name === "AbortError") return;
		// eslint-disable-next-line no-console
		console.warn("[notificationSound] ringtone failed", error);
		if (error?.name === "NotAllowedError") return;

		if (element.getAttribute("src") !== DEFAULT_CALL_SOUND_URL) {
			element.src = DEFAULT_CALL_SOUND_URL;
			element.play().catch(() => undefined);
		}
	});
}

/** Проигрыватель разового звука: новый запуск прерывает предыдущий. */
export function createSoundPlayer() {
	let current: HTMLAudioElement | null = null;
	let stopTimer: ReturnType<typeof setTimeout> | null = null;
	let playId = 0;

	const stop = () => {
		playId += 1;
		if (stopTimer) clearTimeout(stopTimer);
		stopTimer = null;
		if (!current) return;
		current.pause();
		current.src = "";
		current = null;
	};

	const start = async (url: string, volume: number) => {
		const audio = new Audio(url);
		audio.volume = toAudioVolume(volume);
		current = audio;

		// Страховка: файл не длиннее лимита, но мало ли.
		stopTimer = setTimeout(() => {
			if (current === audio) stop();
		}, (SOUND_MAX_SECONDS + 1) * 1000);

		await audio.play();
	};

	return {
		stop,

		setVolume(volume: number) {
			if (current) current.volume = toAudioVolume(volume);
		},

		async play(
			sound: SoundRef | null | undefined,
			fallbackUrl: string,
			volume: number,
		) {
			stop();
			playId += 1;
			const id = playId;

			const url = await resolveSoundUrl(sound, fallbackUrl);
			if (id !== playId) return;

			try {
				await start(url, volume);
			} catch (error: any) {
				if (error?.name === "AbortError" || id !== playId) return;
				// eslint-disable-next-line no-console
				console.warn("[notificationSound] play failed", error);
				stop();

				// Автоплей заблокирован — не страшно. Битый файл — стандартный звук.
				if (error?.name === "NotAllowedError" || url === fallbackUrl) {
					return;
				}
				await start(fallbackUrl, volume).catch(() => stop());
			}
		},
	};
}

const onlineOrderPlayer = createSoundPlayer();

export function playOnlineOrderSound(
	sound: SoundRef | null | undefined,
	volume: number,
) {
	// Новый заказ во время звука — перезапускаем звук, а не накладываем.
	return onlineOrderPlayer.play(
		sound,
		DEFAULT_ONLINE_ORDER_SOUND_URL,
		volume,
	);
}
